import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'minitask_cookie_consent';

/**
 * Cookie consent options
 */
const CONSENT_OPTIONS = {
  necessary: true, // Always required
  analytics: false,
  functional: false
};

/**
 * CookieBanner Component
 * Shows a cookie consent banner on first visit
 * Saves consent to localStorage
 */
const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState(CONSENT_OPTIONS);

  // Check if consent has been given
  useEffect(() => {
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!savedConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Save consent and hide banner
  const saveConsent = (consentData) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      ...consentData,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);

    // If analytics is accepted, initialize Google Analytics
    if (consentData.analytics && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  // Accept all cookies
  const handleAcceptAll = () => {
    const allConsent = {
      necessary: true,
      analytics: true,
      functional: true
    };
    saveConsent(allConsent);
  };

  // Accept only necessary cookies
  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      functional: false
    };
    saveConsent(necessaryOnly);
  };

  // Save custom preferences
  const handleSavePreferences = () => {
    saveConsent(consent);
  };

  // Toggle individual consent
  const toggleConsent = (key) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setConsent(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40 z-[60] backdrop-blur-sm" />
      
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[70] p-4 md:p-6">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Main content */}
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="hidden md:flex w-12 h-12 bg-[#0A1929] rounded-xl items-center justify-center flex-shrink-0">
                <span className="material-symbols-outlined text-white text-2xl">cookie</span>
              </div>
              
              <div className="flex-1">
                <h2 className="font-heading font-bold text-xl text-[#0A1929] mb-2">
                  Vi bruker cookies
                </h2>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Vi bruker cookies for å forbedre din opplevelse på Minitask. Noen cookies er 
                  nødvendige for at nettsiden skal fungere, mens andre hjelper oss å forstå 
                  hvordan du bruker tjenesten.{' '}
                  <Link to="/personvern" className="text-[#1E3A5F] hover:underline font-medium">
                    Les mer i vår personvernerklæring
                  </Link>
                </p>
              </div>
            </div>

            {/* Detailed options */}
            {showDetails && (
              <div className="mt-6 pt-6 border-t border-slate-100 space-y-4">
                {/* Necessary cookies */}
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-[#0A1929] flex items-center gap-2">
                      <span className="material-symbols-outlined text-green-600 text-lg">check_circle</span>
                      Nødvendige cookies
                    </h4>
                    <p className="text-sm text-slate-500 mt-1">
                      Påkrevd for at nettsiden skal fungere. Kan ikke slås av.
                    </p>
                  </div>
                  <div className="w-12 h-6 bg-green-500 rounded-full flex items-center justify-end px-1 cursor-not-allowed">
                    <div className="w-4 h-4 bg-white rounded-full shadow" />
                  </div>
                </div>

                {/* Analytics cookies */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-[#0A1929] flex items-center gap-2">
                      <span className="material-symbols-outlined text-blue-600 text-lg">analytics</span>
                      Analytiske cookies
                    </h4>
                    <p className="text-sm text-slate-500 mt-1">
                      Hjelper oss å forstå hvordan nettsiden brukes (Google Analytics).
                    </p>
                  </div>
                  <button
                    onClick={() => toggleConsent('analytics')}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      consent.analytics ? 'bg-[#1E3A5F] justify-end' : 'bg-slate-300 justify-start'
                    }`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full shadow" />
                  </button>
                </div>

                {/* Functional cookies */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-[#0A1929] flex items-center gap-2">
                      <span className="material-symbols-outlined text-purple-600 text-lg">tune</span>
                      Funksjonelle cookies
                    </h4>
                    <p className="text-sm text-slate-500 mt-1">
                      Husker dine preferanser som lokasjon og visningsinnstillinger.
                    </p>
                  </div>
                  <button
                    onClick={() => toggleConsent('functional')}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      consent.functional ? 'bg-[#1E3A5F] justify-end' : 'bg-slate-300 justify-start'
                    }`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full shadow" />
                  </button>
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              {!showDetails ? (
                <>
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 bg-[#0A1929] text-white py-3 px-6 rounded-xl font-medium hover:bg-[#1E3A5F] transition-colors"
                  >
                    Godta alle
                  </button>
                  <button
                    onClick={handleAcceptNecessary}
                    className="flex-1 bg-slate-100 text-[#0A1929] py-3 px-6 rounded-xl font-medium hover:bg-slate-200 transition-colors"
                  >
                    Kun nødvendige
                  </button>
                  <button
                    onClick={() => setShowDetails(true)}
                    className="sm:flex-none text-[#1E3A5F] py-3 px-6 rounded-xl font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-1"
                  >
                    <span className="material-symbols-outlined text-lg">settings</span>
                    Tilpass
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleSavePreferences}
                    className="flex-1 bg-[#0A1929] text-white py-3 px-6 rounded-xl font-medium hover:bg-[#1E3A5F] transition-colors"
                  >
                    Lagre preferanser
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 bg-slate-100 text-[#0A1929] py-3 px-6 rounded-xl font-medium hover:bg-slate-200 transition-colors"
                  >
                    Godta alle
                  </button>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="sm:flex-none text-slate-500 py-3 px-6 rounded-xl font-medium hover:text-[#0A1929] transition-colors"
                  >
                    Tilbake
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * Get current cookie consent
 * @returns {Object|null} Consent object or null if not set
 */
export const getCookieConsent = () => {
  const saved = localStorage.getItem(COOKIE_CONSENT_KEY);
  return saved ? JSON.parse(saved) : null;
};

/**
 * Check if a specific cookie type is allowed
 * @param {string} type - 'necessary', 'analytics', or 'functional'
 * @returns {boolean}
 */
export const isCookieAllowed = (type) => {
  const consent = getCookieConsent();
  if (!consent) return type === 'necessary';
  return consent[type] === true;
};

/**
 * Reset cookie consent (for testing or settings page)
 */
export const resetCookieConsent = () => {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
};

export default CookieBanner;
